import React, { useState, useEffect } from 'react';
import { settingsAPI, authAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';
import Autocomplete from 'react-google-autocomplete';
import CoachSidebar from '../components/Dashboard/CoachSidebar';
import EditAvatarModal from '../components/Modals/EditAvatarModal';
import EditPromptModal from '../components/Modals/EditPromptModal';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);

  // Form states
  const [profileData, setProfileData] = useState({
    name: '',
    city: '',
    language: '',
    dob: '',
    description: '',
    specialization: '',
    chatWelcomeMessage: '',
    videoWelcomeMessage: '',
    system_prompt: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        city: user.city || '',
        language: user.language || '',
        dob: user.dob ? new Date(user.dob).toISOString().split('T')[0] : '',
        description: user.description || '',
        specialization: user.specialization || '',
        chatWelcomeMessage: user.chatWelcomeMessage || '',
        videoWelcomeMessage: user.videoWelcomeMessage || '',
        system_prompt: user.coachSettings?.config?.system_prompt || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const dataToSend = { ...profileData };
      
      // Backend expects date-time format (ISO string)
      if (dataToSend.dob) {
        const dobDate = new Date(dataToSend.dob);
        if (!isNaN(dobDate.getTime())) {
          dataToSend.dob = dobDate.toISOString();
        }
      }

      // Sync system prompt to coachSettings
      if (dataToSend.system_prompt !== undefined) {
        dataToSend.coachSettings = {
          config: {
            system_prompt: dataToSend.system_prompt
          }
        };
        // Clean up internal state field before sending to backend if backend doesn't expect it directly
        // But since we are sending it in coachSettings, it's fine.
      }

      const response = await authAPI.updateMe(dataToSend);
      updateUser(response.data.user);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Only images are allowed for profile picture');
      return;
    }

    setAvatarUploading(true);
    setError('');
    setSuccess('');

    try {
      const response = await settingsAPI.uploadAvatar(file);
      updateUser({ ...user, avatarUrl: response.data.avatarUrl });
      setSuccess('Profile picture updated');
    } catch (err) {
      setError('Error uploading avatar');
    } finally {
      setAvatarUploading(false);
      event.target.value = '';
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedVideoTypes.includes(file.type)) {
      setError('Only MP4, MOV, AVI videos are allowed');
      return;
    }

    setVideoUploading(true);
    setError('');
    setSuccess('');

    try {
      const response = await settingsAPI.uploadVideoAvatar(file);
      updateUser({ ...user, avatar: response.data.avatar });
      setSuccess('Video avatar uploaded successfully');
    } catch (err) {
      setError('Error uploading video avatar');
    } finally {
      setVideoUploading(false);
      event.target.value = '';
    }
  };

  const handleVoiceUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedAudioTypes = ['audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/ogg'];
    if (!allowedAudioTypes.includes(file.type)) {
      setError('Only MP3, WAV, OGG audio files are allowed');
      return;
    }

    setVoiceUploading(true);
    setError('');
    setSuccess('');

    try {
      const response = await settingsAPI.uploadVoice(file);
      updateUser({ ...user, voice: response.data.voice });
      setSuccess('Voice sample uploaded successfully');
    } catch (err) {
      setError('Error uploading voice sample');
    } finally {
      setVoiceUploading(false);
      event.target.value = '';
    }
  };

  const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('blob:')) return path;
    return `${process.env.REACT_APP_API_URL}${path}`;
  };

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      
      <EditAvatarModal isOpen={isAvatarModalOpen} onClose={() => setIsAvatarModalOpen(false)} currentAvatar={user?.avatarUrl} />
      <EditPromptModal isOpen={isPromptModalOpen} onClose={() => setIsPromptModalOpen(false)} />

      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
            
            <main className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                 <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Settings</h1>
                 <button 
                   onClick={handleSave}
                   disabled={loading}
                   className="bg-[var(--tangerine)] hover:bg-[#d97700] shadow-md shadow-orange-500/20 text-white font-bold px-6 py-2.5 rounded-xl transition-all hover:-translate-y-0.5 text-sm disabled:opacity-50"
                 >
                   {loading ? 'Saving...' : 'Save All Changes'}
                 </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-6">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded mb-6">
                  {success}
                </div>
              )}

              <div className="space-y-6 pb-10">
                {/* Personal Information Section */}
                <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-bold text-[var(--text-primary)] mb-5">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={profileData.name}
                          onChange={handleInputChange}
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-3 shadow-inner transition-colors"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
                          City
                        </label>
                        <Autocomplete
                          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                          onPlaceSelected={(place) => {
                            setProfileData(prev => ({
                              ...prev,
                              city: place.formatted_address || place.name || ''
                            }));
                          }}
                          options={{
                            types: ['(cities)'],
                          }}
                          name="city"
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-3 shadow-inner transition-colors"
                          value={profileData.city}
                          onChange={handleInputChange}
                          placeholder="Start typing your city..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
                          Language
                        </label>
                        <select
                          name="language"
                          value={profileData.language}
                          onChange={handleInputChange}
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-3 shadow-inner transition-colors"
                        >
                          <option value="English">English</option>
                          <option value="Spanish">Spanish</option>
                          <option value="French">French</option>
                          <option value="German">German</option>
                          <option value="Chinese">Chinese</option>
                          <option value="Japanese">Japanese</option>
                          <option value="Russian">Russian</option>
                          <option value="Arabic">Arabic</option>
                          <option value="Hindi">Hindi</option>
                          <option value="Portuguese">Portuguese</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          name="dob"
                          value={profileData.dob}
                          onChange={handleInputChange}
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-3 shadow-inner transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Profile Picture Section */}
                <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-bold text-[var(--text-primary)] mb-4">
                      Profile Picture
                    </h3>
                    <div className="flex items-center gap-6">
                      <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden border-2 border-[var(--tangerine)] flex-shrink-0">
                        {user?.avatarUrl ? (
                          <img src={getFullUrl(user.avatarUrl)} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[var(--text-secondary)]/60">
                             <span className="material-symbols-outlined text-4xl">person</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[var(--text-secondary)] mb-3">Upload a static photo for your profile.</p>
                        <label className="inline-block bg-[var(--surface-white)] border border-[var(--border-light)] hover:bg-[var(--off-white)] text-[var(--text-secondary)] font-bold px-5 py-2.5 rounded-xl cursor-pointer transition-all text-sm">
                          {avatarUploading ? 'Uploading...' : 'Choose Photo'}
                          <input type="file" accept="image/*" onChange={handleAvatarUpload} disabled={avatarUploading} className="hidden" />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Avatar Video Section */}
                <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-bold text-[var(--text-primary)] mb-4">
                      AI Avatar (Video)
                    </h3>
                    <div className="mt-2 text-sm text-[var(--text-secondary)] mb-4">
                      <p>Upload a video to create your interactive AI Avatar. Supported formats: MP4, MOV, AVI.</p>
                    </div>
                    <div className="flex flex-col gap-4">
                      <div>
                        <label className="inline-block bg-[var(--tangerine)] hover:bg-[#d97700] shadow-md shadow-orange-500/20 text-white font-bold px-6 py-3 rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 text-sm">
                          {videoUploading ? 'Uploading Video...' : 'Choose Video File'}
                          <input type="file" accept="video/*" onChange={handleVideoUpload} disabled={videoUploading} className="hidden" />
                        </label>
                      </div>
                      
                      {user?.avatar?.url && (
                        <div className="p-3 bg-[var(--tangerine)]/10 border-[var(--tangerine)]/20 flex items-center gap-3">
                           <span className="material-symbols-outlined text-[var(--tangerine)]">videocam</span>
                           <div className="flex-1 min-w-0">
                             <p className="text-xs font-bold text-[var(--tangerine)] uppercase">Current AI Avatar Video</p>
                             <p className="text-sm text-[var(--text-secondary)] truncate">{user.avatar.name || 'video_avatar.mp4'}</p>
                           </div>
                           <a href={getFullUrl(user.avatar.url)} target="_blank" rel="noreferrer" className="text-xs font-bold text-[var(--tangerine)] hover:underline">Preview</a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Voice Upload Section */}
                <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-bold text-[var(--text-primary)] mb-4">
                      Voice Upload
                    </h3>
                    <div className="mt-2 text-sm text-[var(--text-secondary)]">
                      <p>Upload a sample of your voice (MP3 or WAV).</p>
                    </div>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                      <label className="inline-block bg-[var(--surface-white)] border text-sm font-bold border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-[var(--off-white)] px-5 py-2.5 rounded-xl transition-colors cursor-pointer">
                        {voiceUploading ? 'Uploading...' : 'Upload Audio File'}
                        <input type="file" accept="audio/*" onChange={handleVoiceUpload} disabled={voiceUploading} className="hidden" />
                      </label>
                      <button className="bg-[var(--surface-white)] border text-sm font-bold border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-[var(--off-white)] px-5 py-2.5 rounded-xl transition-colors shrink-0 flex items-center justify-center gap-2">
                        <span className="material-symbols-outlined text-lg">mic</span>
                        Record Audio (Soon)
                      </button>
                    </div>
                    {user?.voice?.url && (
                      <div className="mt-4 p-4 bg-[var(--off-white)] rounded-xl border border-[var(--border-light)]">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-bold text-[var(--text-secondary)] uppercase">Current Voice Sample</p>
                          <p className="text-xs text-[var(--text-secondary)]/60">{user.voice.name || 'voice.mp3'}</p>
                        </div>
                        <audio controls className="w-full h-10">
                          <source src={getFullUrl(user.voice.url)} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                  </div>
                </div>

                {/* Profile Settings Section */}
                <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-bold text-[var(--text-primary)] mb-5">
                      Coach Profile
                    </h3>
                    <div className="space-y-5">
                      <div>
                        <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
                          Description / Bio
                        </label>
                        <textarea
                          rows="4"
                          name="description"
                          value={profileData.description}
                          onChange={handleInputChange}
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-4 shadow-inner resize-none transition-colors"
                          placeholder="Tell us about yourself and your coaching experience..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
                          Specialization
                        </label>
                        <input
                          type="text"
                          name="specialization"
                          value={profileData.specialization}
                          onChange={handleInputChange}
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-3 shadow-inner transition-colors"
                          placeholder="e.g., career coaching, personal growth"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Chat Settings Section */}
                <div className="bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-bold text-[var(--text-primary)] mb-5">
                      AI Chat Settings
                    </h3>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide">
                            Chat Welcome Message
                          </label>
                          <span className="text-[10px] font-bold text-[var(--text-secondary)]/60">{profileData.chatWelcomeMessage.length}/200</span>
                        </div>
                        <textarea
                          rows="2"
                          name="chatWelcomeMessage"
                          maxLength={200}
                          value={profileData.chatWelcomeMessage}
                          onChange={handleInputChange}
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-3 shadow-inner resize-none transition-colors"
                          placeholder="Hello! I am your AI coach. How can I help you today?"
                        />
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide">
                            Video Welcome Message
                          </label>
                          <span className="text-[10px] font-bold text-[var(--text-secondary)]/60">{profileData.videoWelcomeMessage.length}/200</span>
                        </div>
                        <textarea
                          rows="2"
                          name="videoWelcomeMessage"
                          maxLength={200}
                          value={profileData.videoWelcomeMessage}
                          onChange={handleInputChange}
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-3 shadow-inner resize-none transition-colors"
                          placeholder="Welcome to our video session! I'm ready when you are."
                        />
                      </div>

                      <div className="pt-2 border-t border-[var(--border-light)]">
                        <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
                          AI System Prompt (Core Personality)
                        </label>
                        <p className="text-[10px] text-[var(--text-secondary)]/60 mb-3 leading-relaxed">
                          Define how your AI assistant behaves, its coaching style, and its primary mission.
                        </p>
                        <textarea
                          rows="8"
                          name="system_prompt"
                          value={profileData.system_prompt}
                          onChange={handleInputChange}
                          className="w-full bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] text-sm rounded-xl focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] block p-4 shadow-inner resize-none transition-colors font-mono"
                          placeholder="Act as a professional life coach..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center pt-4">
                  <button 
                    onClick={handleSave}
                    disabled={loading}
                    className="w-full sm:w-auto bg-[var(--tangerine)] hover:bg-[#d97700] shadow-xl shadow-orange-500/20 text-white font-black px-12 py-4 rounded-2xl transition-all hover:-translate-y-1 text-base disabled:opacity-50 active:scale-95"
                  >
                    {loading ? 'Saving Changes...' : 'Save All Changes'}
                  </button>
                </div>

              </div>
            </main>

            <CoachSidebar 
              activeTab="settings"
              onOpenAvatarModal={() => setIsAvatarModalOpen(true)}
              onOpenPromptModal={() => setIsPromptModalOpen(true)}
              onOpenSettingsModal={() => { window.location.href = '/coach/settings' }}
            />

          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
